import { useState, useEffect } from 'react';
import { Loader2, Zap, AlertTriangle, TrendingDown, Check } from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import { useUIStore } from '@/stores/ui-store';

/**
 * AI credits: balance, burn rate, top-up, and auto top-off controls.
 *
 * Auto top-off charges a saved card without anyone watching, so this screen
 * exists to make that legible: what the balance is, when it runs out, what the
 * ceiling is, and every grant that has ever topped it up. Control isn't real if
 * the person paying can't see what it did.
 */

interface CreditBalance {
  allowance: number | null;
  granted: number;
  used: number;
  remaining: number | null;
  periodStart: string;
  unmetered: boolean;
}

interface TopupSettings {
  autoTopupEnabled: boolean;
  thresholdCredits: number;
  packCredits: number;
  monthlyCapUsd: number;
  notifyAtPct: number;
  disabledReason: string | null;
  lastAutoTopupAt: string | null;
}

interface Grant {
  id: string;
  kind: string;
  credits: number;
  credits_used: number;
  auto_topup: boolean;
  note: string | null;
  created_at: string;
}

interface Projection {
  creditsPerDay: number;
  daysRemaining: number;
  exhaustsAt: string;
}

interface CreditsResponse {
  balance: CreditBalance;
  settings: TopupSettings;
  pack: { credits: number; priceUsd: number };
  grants: Grant[];
  projection: Projection | null;
}

export default function AiCreditsSection() {
  const [data, setData] = useState<CreditsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<TopupSettings | null>(null);
  const showToast = useUIStore((s) => s.addToast);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const res = await api.get<CreditsResponse>('/admin/ai/credits');
      setData(res);
      setForm(res.settings);
    } catch {
      showToast('Could not load AI credit status', 'error');
    } finally {
      setLoading(false);
    }
  }

  async function saveSettings(patch: Partial<TopupSettings>) {
    if (!form) return;
    setSaving(true);
    try {
      const res = await api.put<{ settings: TopupSettings }>(
        '/admin/ai/credits/auto-topup',
        patch,
      );
      setForm(res.settings);
      setData((d) => (d ? { ...d, settings: res.settings } : d));
      showToast('Auto top-off settings saved', 'success');
    } catch (err: any) {
      showToast(err?.message || 'Could not save settings', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function buyTopup() {
    try {
      const res = await api.post<{ url: string }>('/admin/ai/credits/topup', {});
      window.location.href = res.url;
    } catch (err: any) {
      showToast(err?.message || 'Could not start checkout', 'error');
    }
  }

  if (loading) {
    return (
      <Card className="p-6">
        <Loader2 className="h-5 w-5 animate-spin text-sage-500" />
      </Card>
    );
  }
  if (!data || !form) return null;

  const { balance, pack, grants, projection } = data;

  if (balance.unmetered) {
    return (
      <Card className="p-6">
        <h3 className="flex items-center gap-2 text-lg font-semibold text-ink">
          <Zap className="h-5 w-5" /> AI credits
        </h3>
        <p className="mt-2 text-sm text-sage-600">
          This site runs unmetered — AI usage isn't drawn from a credit allowance.
        </p>
      </Card>
    );
  }

  const total = (balance.allowance ?? 0) + balance.granted;
  const usedPct = total > 0 ? Math.min(100, (balance.used / total) * 100) : 0;
  const low = balance.remaining !== null && balance.remaining <= form.thresholdCredits;

  return (
    <Card className="p-6">
      <h3 className="flex items-center gap-2 text-lg font-semibold text-ink">
        <Zap className="h-5 w-5" /> AI credits
      </h3>

      {/* Balance */}
      <div className="mt-4">
        <div className="flex items-baseline justify-between">
          <span className="text-2xl font-semibold tabular-nums text-ink">
            {balance.remaining?.toLocaleString()}
          </span>
          <span className="text-sm text-sage-600">
            of {total.toLocaleString()} this period
          </span>
        </div>
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-sage-200">
          <div
            className={`h-full rounded-full ${low ? 'bg-terra-500' : 'bg-sage-500'}`}
            style={{ width: `${usedPct}%` }}
          />
        </div>
        {projection && (
          <p className="mt-2 flex items-center gap-1.5 text-sm text-sage-600">
            <TrendingDown className="h-4 w-4" />
            About {projection.creditsPerDay.toLocaleString()} credits a day — roughly{' '}
            {projection.daysRemaining.toFixed(0)} days left at this rate.
          </p>
        )}
      </div>

      {form.disabledReason && (
        <div className="mt-4 flex items-start gap-2 rounded-lg bg-terra-50 p-3 text-sm text-terra-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Auto top-off was paused: {form.disabledReason}. Re-enable it below once
            that's sorted.
          </span>
        </div>
      )}

      {/* Manual top-up */}
      <div className="mt-6 flex items-center justify-between rounded-lg border border-sage-200 p-4">
        <div>
          <p className="text-sm font-medium text-ink">
            Top up {pack.credits.toLocaleString()} credits for ${pack.priceUsd}
          </p>
          <p className="text-sm text-sage-600">Top-ups roll over and never expire.</p>
        </div>
        <Button onClick={buyTopup}>Buy credits</Button>
      </div>

      {/* Auto top-off */}
      <div className="mt-6 border-t border-sage-200 pt-6">
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={form.autoTopupEnabled}
            disabled={saving}
            onChange={(e) =>
              setForm({ ...form, autoTopupEnabled: e.target.checked })
            }
            className="h-4 w-4 rounded border-sage-300"
          />
          <span className="text-sm font-medium text-ink">
            Buy more automatically when I run low
          </span>
        </label>
        <p className="mt-1 pl-7 text-sm text-sage-600">
          We'll charge your saved card for one {pack.credits.toLocaleString()}-credit
          pack whenever the balance drops below your threshold — never past the
          monthly limit you set here.
        </p>

        <div className="mt-4 grid gap-4 pl-7 sm:grid-cols-3">
          <label className="block">
            <span className="text-sm text-sage-700">Top up below</span>
            <input
              type="number"
              min={0}
              value={form.thresholdCredits}
              disabled={saving}
              onChange={(e) =>
                setForm({ ...form, thresholdCredits: Number(e.target.value) })
              }
              className="mt-1 w-full rounded-lg border border-sage-300 px-3 py-2 text-sm"
            />
            <span className="text-xs text-sage-500">credits remaining</span>
          </label>

          <label className="block">
            <span className="text-sm text-sage-700">Never spend more than</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={form.monthlyCapUsd}
              disabled={saving}
              onChange={(e) =>
                setForm({ ...form, monthlyCapUsd: Number(e.target.value) })
              }
              className="mt-1 w-full rounded-lg border border-sage-300 px-3 py-2 text-sm"
            />
            <span className="text-xs text-sage-500">USD per month — a hard limit</span>
          </label>

          <label className="block">
            <span className="text-sm text-sage-700">Warn me at</span>
            <input
              type="number"
              min={1}
              max={100}
              value={form.notifyAtPct}
              disabled={saving}
              onChange={(e) =>
                setForm({ ...form, notifyAtPct: Number(e.target.value) })
              }
              className="mt-1 w-full rounded-lg border border-sage-300 px-3 py-2 text-sm"
            />
            <span className="text-xs text-sage-500">% of credits used</span>
          </label>
        </div>

        <div className="mt-4 pl-7">
          <Button
            onClick={() => saveSettings(form)}
            disabled={saving}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
          </Button>
          {form.autoTopupEnabled && form.monthlyCapUsd <= 0 && (
            <p className="mt-2 text-sm text-terra-700">
              Set a monthly limit above $0 to turn auto top-off on.
            </p>
          )}
        </div>
      </div>

      {/* Ledger */}
      {grants.length > 0 && (
        <div className="mt-6 border-t border-sage-200 pt-6">
          <h4 className="text-sm font-medium text-ink">Credit history</h4>
          <table className="mt-3 w-full text-sm">
            <tbody>
              {grants.map((g) => (
                <tr key={g.id} className="border-b border-sage-100 last:border-0">
                  <td className="py-2 text-sage-700">
                    {new Date(g.created_at).toLocaleDateString()}
                  </td>
                  <td className="py-2 text-sage-700">
                    {g.kind === 'launch'
                      ? 'Launch grant'
                      : g.auto_topup
                        ? 'Automatic top-up'
                        : g.kind === 'topup'
                          ? 'Top-up purchase'
                          : 'Credit'}
                  </td>
                  <td className="py-2 text-right tabular-nums text-sage-800">
                    +{g.credits.toLocaleString()}
                  </td>
                  <td className="py-2 pl-4 text-right text-xs text-sage-500">
                    {g.credits_used >= g.credits ? (
                      'used up'
                    ) : (
                      <span className="inline-flex items-center gap-1">
                        <Check className="h-3 w-3" /> available
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
